﻿import { defineComponent, PropType } from 'vue';
import { useBookingStore } from '../../../store/bookingstore';

import { passenger } from "../../../objects/v1_2/Booking/Passenger";
import { country_nationality } from "../../../objects/v1_3/web_engine/country_nationality";

import {
    Empty_Draft, Format_Date_Of_Birth, Format_Phone, Is_Child, Is_Required, Is_Visible, Rules_Context, Validate_Passenger,
    Passenger_Draft, Passenger_Errors, Passenger_Field, Passenger_Rules_Context
} from "./passenger_rules";

type Phone_Kind = 'telephone' | 'emergency';

export default defineComponent({
    name: "passenger_form",
    props: {
        passenger: {
            type: Object as PropType<passenger>,
            required: true
        },
        index: {
            type: Number,
            required: true
        },
        countries: {
            type: Array as PropType<country_nationality[]>,
            default: () => []
        }
    },
    setup() {
        const bookingStore = useBookingStore();

        return {
            bookingStore
        }
    },
    data() {
        return {
            touched: {} as Partial<Record<Passenger_Field, boolean>>
        }
    },
    computed: {
        ctx(): Passenger_Rules_Context {
            return Rules_Context(this.$store.getters.get_Booking);
        },
        draft(): Passenger_Draft {
            // Created by the parent list before this form renders.
            return this.bookingStore.passenger_drafts[this.passenger.passenger_identifier ?? ""] ?? Empty_Draft();
        },
        errors(): Passenger_Errors {
            return Validate_Passenger(this.passenger, this.draft, this.ctx);
        },
        Error_Count(): number {
            return Object.keys(this.errors).length;
        },
        Full_Name(): string {
            return ((this.passenger.first_name ?? "") + " " + (this.passenger.family_name ?? "")).trim();
        },
        Dial_Codes(): country_nationality[] {
            return this.countries.filter(c => /^\d+$/.test(c.dialing_code ?? ""));
        },
        Nationalities(): country_nationality[] {
            return this.countries.filter(c => (c.iso_country_code ?? "") !== "");
        },
        Show_Contact_Section(): boolean {
            return this.Visible('email') || this.Visible('telephone_number');
        },
        Show_Emergency_Section(): boolean {
            return this.Visible('emergency_contact') || this.Visible('emergency_number');
        },
        Show_Newsletter(): boolean {
            return !this.isAgent_Login() && this.index === 0 && this.Required('email');
        }
    },
    methods: {
        Uid(field: string): string {
            return "pax" + this.index + "_" + field;
        },
        Visible(field: Passenger_Field): boolean {
            return Is_Visible(this.passenger, field, this.ctx);
        },
        Required(field: Passenger_Field): boolean {
            return Is_Required(this.passenger, field, this.ctx);
        },
        Show_Error(field: Passenger_Field): boolean {
            return !!this.errors[field] && (this.bookingStore.show_passenger_errors || !!this.touched[field]);
        },
        Described_By(field: Passenger_Field, hint: boolean = false): string | undefined {
            const ids: string[] = [];
            if (hint) {
                ids.push(this.Uid(field) + "_hint");
            }
            if (this.Show_Error(field)) {
                ids.push(this.Uid(field) + "_error");
            }
            return ids.length ? ids.join(" ") : undefined;
        },
        Touch(field: Passenger_Field) {
            this.touched[field] = true;
        },
        // Grouped inputs (date of birth, phone) count as touched once focus leaves the whole group.
        Touch_Group(field: Passenger_Field, event: FocusEvent) {
            const group = event.currentTarget as HTMLElement | null;
            const next = event.relatedTarget as Node | null;
            if (!group || !next || !group.contains(next)) {
                this.Touch(field);
            }
        },
        Update_Date_Of_Birth() {
            this.passenger.date_of_birth = Format_Date_Of_Birth(this.draft);
        },
        Update_Phone(kind: Phone_Kind) {
            if (kind === 'telephone') {
                this.passenger.telephone_number = Format_Phone(this.draft.phone_code, this.draft.phone_number);
            } else {
                this.passenger.emergency_number = Format_Phone(this.draft.emergency_code, this.draft.emergency_number);
            }
        },
        isChild(): boolean {
            return Is_Child(this.passenger);
        },
        isAgent_Login(): boolean {
            return this.$store.getters.get_Agent_Login?.valid_login == true;
        },
        getIsDebug(): boolean {
            return Boolean(import.meta.env.VITE_DEFAULT_IS_DEBUG);
        },
        // Staging only: fills the form with test data.
        Autofill() {
            const names = ["John Doe", "Jane Smith", "Alice Johnson", "Bob Brown", "Charlie Davis", "Emily Wilson", "Frank Taylor", "Grace Anderson", "Henry Thomas", "Ivy Moore"];
            const [first, last] = names[this.index % names.length].split(" ");

            this.passenger.first_name = first;
            this.passenger.family_name = last;
            this.passenger.email = "test@ttc.com";
            this.passenger.gender = "Male";
            this.passenger.iso_nationality = this.Nationalities.find(c => c.country === "United Kingdom")?.iso_country_code ?? this.passenger.iso_nationality;
            this.passenger.emergency_contact = "Test Contact";
            this.passenger.post_code = "EH1 1AA";

            this.draft.confirm_email = "test@ttc.com";
            this.draft.phone_code = "44";
            this.draft.phone_number = "7700900123";
            this.draft.emergency_code = "44";
            this.draft.emergency_number = "7700900456";
            this.draft.dob_day = "01";
            this.draft.dob_month = "01";
            this.draft.dob_year = this.isChild() ? String(new Date().getFullYear() - 10) : "1970";

            this.Update_Date_Of_Birth();
            this.Update_Phone('telephone');
            this.Update_Phone('emergency');
        }
    }
})
